// Admin > Contact directory (flow plan stage B item 8): the organisation's
// curated, sourced list of public organisational contacts (RCAs, Metlink,
// FENZ, utilities, councillors). Backend: routers/contact_directory.py.
// Shipped empty — every entry must name where it is published.
import { getJson, patchJson, postJson } from "./api";
import { el } from "./legend";
import "./styles/flowStageB.css";
import { showToast } from "./toast";

// not imported from flowNav: that pulls the map (deck.gl) into the field
// app's boot bundle via admin.ts (main.ts, 2026-10-05)
const errMessage = (err: unknown): string => (err instanceof Error ? err.message : String(err));

interface Entry {
  id: number; organisation: string; category: string | null; role: string | null; name: string | null; email: string | null;
  phone: string | null; website: string | null; source: string; source_url: string | null; verified_at: string | null;
  notes: string | null; active: boolean; contact_status: string;
}

const FIELDS: [keyof Entry, string][] = [["organisation", "Organisation *"], ["category", "Category"], ["role", "Role"], ["name", "Name"],
  ["email", "Email"], ["phone", "Phone"], ["website", "Website"], ["source", "Source * (where it is published)"],
  ["source_url", "Source URL"], ["verified_at", "Verified (YYYY-MM-DD)"], ["notes", "Notes"]];

function form(categories: string[], entry: Partial<Entry>, onSave: (body: Record<string, unknown>) => void): HTMLElement {
  const box = el("div", { className: "fb-dir-form" });
  const inputs: Record<string, HTMLInputElement | HTMLSelectElement> = {};
  for (const [k, label] of FIELDS) {
    let inp: HTMLInputElement | HTMLSelectElement;
    if (k === "category") {
      inp = el("select", {});
      inp.append(el("option", { value: "" }, "—"));
      for (const c of categories) inp.append(el("option", { value: c }, c));
      inp.value = (entry.category as string) ?? "";
    } else inp = el("input", { value: (entry[k] as string | null | undefined) ?? "", className: "fb-input-sm", placeholder: label });
    inputs[k] = inp;
    const l = el("label", { className: "fb-kv" });
    l.append(el("span", { className: "fb-k" }, label), inp);
    box.append(l);
  }
  const save = el("button", { type: "button" }, "Save");
  save.addEventListener("click", () => {
    const body: Record<string, unknown> = {};
    for (const [k] of FIELDS) body[k] = inputs[k].value || null;
    onSave(body);
  });
  box.append(save);
  return box;
}

export function renderContactDirectorySection(root: HTMLElement): void {
  root.replaceChildren(el("p", { className: "caption" }, "Loading…"));
  getJson<{ entries: Entry[]; categories: string[]; can_edit: boolean }>("/api/contact-directory?include_inactive=true")
    .then(({ entries, categories, can_edit }) => {
      root.replaceChildren(el("h3", {}, "Contact directory"),
        el("p", { className: "caption" }, "Public, role-level organisational contacts for stakeholder lists (RCA corridor access, Metlink, FENZ, utilities, councillors). Each entry must name its published source and the date it was checked — nothing is guessed. Analysts pick from this list on a work brief."));
      const reload = () => renderContactDirectorySection(root);
      if (can_edit) {
        const add = el("button", { type: "button" }, "+ Add contact");
        const mount = el("div", {});
        add.addEventListener("click", () => mount.replaceChildren(form(categories, {}, (body) => {
          postJson("/api/contact-directory", body).then(reload).catch((e) => showToast(`Failed: ${errMessage(e)}`));
        })));
        root.append(add, mount);
      }
      if (!entries.length) {
        root.append(el("p", { className: "caption" }, "The directory is empty."));
        return;
      }
      const t = el("table", { className: "detail-table fb-table" });
      const h = el("tr", {});
      for (const c of ["Organisation", "Category", "Role / name", "Email", "Phone", "Source", "Verified", ""]) h.append(el("th", {}, c));
      t.append(h);
      for (const e of entries) {
        const tr = el("tr", { className: e.active ? "" : "fb-inactive" });
        tr.append(el("td", {}, e.organisation), el("td", {}, e.category ?? ""), el("td", {}, [e.role, e.name].filter(Boolean).join(" — ")),
          el("td", {}, e.email ?? (e.contact_status === "placeholder" ? "placeholder" : "")), el("td", {}, e.phone ?? ""),
          el("td", {}, e.source), el("td", {}, e.verified_at ?? ""));
        const td = el("td", {});
        if (can_edit) {
          const edit = el("button", { type: "button" }, "Edit");
          edit.addEventListener("click", () => {
            const row = el("tr", {});
            const cell = el("td", { colSpan: 8 });
            cell.append(form(categories, e, (body) => {
              patchJson(`/api/contact-directory/${e.id}`, body).then(reload).catch((err) => showToast(`Failed: ${errMessage(err)}`));
            }));
            row.append(cell);
            tr.after(row);
          });
          const tog = el("button", { type: "button" }, e.active ? "Deactivate" : "Reactivate");
          tog.addEventListener("click", () => {
            patchJson(`/api/contact-directory/${e.id}`, { active: !e.active }).then(reload).catch((err) => showToast(`Failed: ${errMessage(err)}`));
          });
          td.append(edit, tog);
        }
        tr.append(td);
        t.append(tr);
      }
      const s = el("div", { className: "admin-table-scroll" });
      s.append(t);
      root.append(s);
    })
    .catch((e) => root.replaceChildren(el("p", { className: "caption" }, `Could not load the directory: ${errMessage(e)}`)));
}
